import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router";
import { ArrowLeft, CheckCircle2, ExternalLink, SearchX } from "lucide-react";
import { useQuizDetail } from "~/api/v2/useQuizDetail";
import { Button } from "~/atoms/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "~/atoms/empty";
import { CenterSpinner } from "~/atoms/spinner";
import { ToggleGroup, ToggleGroupItem } from "~/atoms/toggle-group";
import HeadingPage from "~/molecules/HeadingPage";
import QuizEditor from "~/organisms/anchoring/QuizEditor";
import QuizTaker from "~/organisms/anchoring/QuizTaker";

type QuizMode = "take" | "edit";

const QuizPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const quizId = Number(id);
  const { data: quiz, error, isLoading, mutate } = useQuizDetail(quizId);

  // `?mode=edit` lands a manager straight in edit mode (e.g. from the
  // anchored documents table).
  const [preferredMode, setPreferredMode] = useState<QuizMode | null>(
    searchParams.get("mode") === "edit" ? "edit" : null,
  );

  if (isLoading) return <CenterSpinner />;

  const backLink = (
    <Button
      variant="ghost"
      size="sm"
      className="text-muted-foreground self-start"
      asChild
    >
      <Link to="/anchoring/me">
        <ArrowLeft data-icon="inline-start" />
        {t(($) => $.anchoring.quiz.back)}
      </Link>
    </Button>
  );

  if (error || !quiz) {
    return (
      <div className="container flex flex-col gap-6 py-7">
        {backLink}
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <SearchX />
            </EmptyMedia>
            <EmptyTitle>{t(($) => $.anchoring.quiz.notFound)}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  // Invited managers can both take and edit; taking is the default when the
  // user was invited, and editing is offered as a toggle.
  const canToggle = quiz.canManage && quiz.invited;
  const requestedMode = preferredMode ?? (quiz.invited ? "take" : "edit");
  const mode: QuizMode = quiz.canManage ? requestedMode : "take";

  return (
    <div className="container flex flex-col gap-6 py-7">
      <div className="flex flex-col gap-4">
        {backLink}
        <div className="flex items-center justify-between gap-4">
          <HeadingPage title={quiz.title} />
          <Button variant="outline" size="sm" asChild>
            <a
              href={`/documents/${quiz.documentId}?releaseId=${quiz.documentReleaseId}`}
              target="_blank"
              rel="noreferrer"
            >
              {t(($) => $.anchoring.quiz.viewDocument)}
              <ExternalLink data-icon="inline-end" />
            </a>
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        {canToggle && (
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            spacing={0}
            value={mode}
            onValueChange={(value) => {
              if (value) setPreferredMode(value as QuizMode);
            }}
          >
            <ToggleGroupItem value="take">
              {t(($) => $.anchoring.quiz.takeQuiz)}
            </ToggleGroupItem>
            <ToggleGroupItem value="edit">
              {t(($) => $.anchoring.manage.edit)}
            </ToggleGroupItem>
          </ToggleGroup>
        )}

        {mode === "edit" ? (
          <QuizEditor quiz={quiz} onSaved={() => void mutate()} />
        ) : quiz.completed ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <CheckCircle2 className="text-success" />
              </EmptyMedia>
              <EmptyTitle>
                {t(($) => $.anchoring.quiz.take.alreadyCompletedTitle)}
              </EmptyTitle>
              <EmptyDescription>
                {t(($) => $.anchoring.quiz.take.alreadyCompletedDescription)}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button asChild>
                <Link to="/anchoring/me">
                  {t(($) => $.anchoring.quiz.take.backToList)}
                </Link>
              </Button>
            </EmptyContent>
          </Empty>
        ) : (
          <QuizTaker quiz={quiz} onCompleted={() => mutate()} />
        )}
      </div>
    </div>
  );
};

export default QuizPage;
